var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { Transition } from '@headlessui/react';
import clsx from 'clsx';
import { Backdrop } from '../Backdrop/index.js';
import { BackgroundProvider } from '../BackgroundContext/index.js';
import { useDocument } from '../hooks/useDocument.js';
import { Portal } from '../Portal/index.js';
import { nodeContainsDescendant, wasContentNodeDescendant, } from '../utils/descendant.js';
import { focusFirstFocusableNode } from '../utils/focus.js';
import { useUniqueId } from '../utils/unique-id.js';
import { useKeypressListener } from '../utils/useKeypressListener.js';
import { ModalPanel } from './components/ModalPanel.js';
import { ModalContext } from './ModalContext.js';
import { ModalActions, ModalFooter } from './ModalFooter.js';
import { ModalHeader } from './ModalHeader.js';
import { labelledById } from './utils.js';
export const Modal = ({ open = false, onClose, children, size = 'lg', className, initialFocus, closeOnBackdropClick = true, activator, showCloseBtn = true, showZenModeBtn = false, }) => {
    const { document } = useDocument();
    const id = useUniqueId('modal');
    const activatorRef = useRef(null);
    const containerNode = useRef(null);
    useEffect(() => {
        if (containerNode.current &&
            !containerNode.current.contains(document.activeElement)) {
            if (initialFocus && initialFocus.current) {
                initialFocus.current.focus();
            }
            else {
                focusFirstFocusableNode(document, containerNode.current);
            }
        }
    }, [document, initialFocus]);
    const [zenMode, setZenMode] = useState(false);
    const toggleZenMode = useCallback(() => {
        setZenMode(zenMode => !zenMode);
    }, []);
    const handleClose = useCallback(() => {
        onClose();
        const activatorElement = activator && isRef(activator)
            ? activator && activator.current
            : activatorRef.current;
        if (activatorElement) {
            requestAnimationFrame(() => focusFirstFocusableNode(document, activatorElement));
        }
    }, [document, activator, onClose]);
    const activatorMarkup = activator && !isRef(activator) ? (React.createElement("div", { ref: activatorRef }, activator)) : null;
    const handleEscape = (event) => {
        if (!open) {
            return;
        }
        const target = event.target;
        const composedPath = event.composedPath();
        const wasDescendant = wasContentNodeDescendant(composedPath, containerNode);
        const isActivatorDescendant = nodeContainsDescendant(activatorRef.current, target);
        if (wasDescendant || isActivatorDescendant) {
            handleClose();
        }
    };
    const handleBackdropClick = () => {
        if (!closeOnBackdropClick) {
            return;
        }
        handleClose();
    };
    const contextValue = useMemo(() => ({
        id,
        open,
        onClose: handleClose,
        showCloseBtn,
        showZenModeBtn,
        zenMode,
        toggleZenMode,
    }), [
        id,
        open,
        handleClose,
        showCloseBtn,
        showZenModeBtn,
        zenMode,
        toggleZenMode,
    ]);
    useKeypressListener('Escape', handleEscape, 'keyup');
    return (React.createElement(React.Fragment, null,
        activatorMarkup,
        React.createElement(Portal, { idPrefix: "modal" },
            React.createElement(ModalContext.Provider, { value: contextValue },
                React.createElement(BackgroundProvider, { backgroundType: "surface" },
                    React.createElement(Transition, { appear: true, show: open },
                        React.createElement("div", { className: clsx('pointer-events-none fixed inset-0 z-10 flex flex-col justify-center', 'overflow-y-hidden overscroll-contain', 'transition-all', !zenMode && 'p-0 sm:p-4', className) },
                            React.createElement(Transition.Child, { enter: "ease-out duration-300", enterFrom: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95", enterTo: "opacity-100 translate-y-0 sm:scale-100", leave: "ease-in duration-200", leaveFrom: "opacity-100 translate-y-0 sm:scale-100", leaveTo: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95", className: "min-h-0" },
                                React.createElement(ModalPanel, { ref: containerNode, "aria-labelledby": labelledById(id), size: size, zenMode: zenMode }, children))),
                        React.createElement(Transition.Child, { enter: "ease-out duration-300", enterFrom: "opacity-0", enterTo: "opacity-100", leave: "ease-in duration-200", leaveFrom: "opacity-100", leaveTo: "opacity-0" },
                            React.createElement(Backdrop, { onClick: handleBackdropClick }))))))));
};
const ModalContent = props => {
    const { children, className, padding = true } = props, rest = __rest(props, ["children", "className", "padding"]);
    return (React.createElement("div", Object.assign({ className: clsx('flex-1', padding && 'px-4 py-5 sm:p-6', className) }, rest), children));
};
Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.Footer = ModalFooter;
Modal.Actions = ModalActions;
function isRef(ref) {
    return Object.prototype.hasOwnProperty.call(ref, 'current');
}
//# sourceMappingURL=Modal.js.map